// =============================================================================
// MeuDriver — Mapa da área de atendimento do instrutor
// -----------------------------------------------------------------------------
// Componente reutilizável: mostra o centro (lat/lng do instrutor) + círculo de
// raio `service_radius_km`. Opcionalmente recebe `pickupLat/pickupLng` para
// também marcar o ponto de embarque do aluno.
//
// Usa react-leaflet (já em deps) com tiles Mapbox/OSM via runtime caching (PWA).
// =============================================================================

import { useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Circle, Popup, useMap } from 'react-leaflet'
import L, { type LatLngExpression } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

// Corrige os ícones default do Leaflet (Webpack/Vite quebra os paths)
// Ref: https://github.com/Leaflet/Leaflet/issues/4968
const DefaultIcon = L.icon({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
})
L.Marker.prototype.options.icon = DefaultIcon

interface Props {
  /** Coordenadas do instrutor (centro do círculo). */
  centerLat: number
  centerLng: number
  /** Raio em km. */
  radiusKm: number
  /** Nome do instrutor para o popup. */
  instructorName?: string
  /** Opcional: ponto de embarque do aluno. */
  pickupLat?: number | null
  pickupLng?: number | null
  pickupAddress?: string | null
  /** Altura customizada (default 320). */
  height?: number | string
  /** className extra (ex: bordas, sombras). */
  className?: string
}

/**
 * Re-centraliza o mapa quando as coordenadas externas mudarem.
 */
function MapCenterUpdater({ center, zoom }: { center: LatLngExpression; zoom: number }) {
  const map = useMap()
  useEffect(() => {
    map.setView(center, zoom)
  }, [center, zoom, map])
  return null
}

function zoomForRadius(radiusKm: number): number {
  // Heurística simples — mantém o círculo enquadrado em ~80% do viewport
  if (radiusKm <= 2) return 13
  if (radiusKm <= 5) return 12
  if (radiusKm <= 10) return 11
  if (radiusKm <= 20) return 10
  if (radiusKm <= 50) return 9
  return 8
}

export function InstructorServiceAreaMap({
  centerLat,
  centerLng,
  radiusKm,
  instructorName,
  pickupLat,
  pickupLng,
  pickupAddress,
  height = 320,
  className,
}: Props) {
  const center: LatLngExpression = [centerLat, centerLng]
  const zoom = zoomForRadius(radiusKm)
  const radiusMeters = Math.max(0.1, radiusKm) * 1000
  const hasPickup =
    typeof pickupLat === 'number' &&
    typeof pickupLng === 'number' &&
    !isNaN(pickupLat) &&
    !isNaN(pickupLng)

  return (
    <div
      className={className}
      style={{
        height,
        width: '100%',
        borderRadius: 12,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapCenterUpdater center={center} zoom={zoom} />
        <Circle
          center={center}
          radius={radiusMeters}
          pathOptions={{
            color: '#1d4ed8',
            fillColor: '#3b82f6',
            fillOpacity: 0.15,
            weight: 2,
          }}
        />
        <Marker position={center}>
          <Popup>
            <strong>{instructorName ?? 'Instrutor'}</strong>
            <br />
            Raio de atendimento: {radiusKm} km
          </Popup>
        </Marker>
        {hasPickup && (
          <Marker position={[pickupLat as number, pickupLng as number]}>
            <Popup>
              <strong>Local de embarque</strong>
              {pickupAddress && (
                <>
                  <br />
                  {pickupAddress}
                </>
              )}
            </Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  )
}
